/**
 * Shared routing-condition type + human-render for the Workflow tab.
 *
 * The shape mirrors the backend `evaluateAutoAdvance` grammar
 * (apps/api/src/services/workflow-engine.ts). A step's
 * autoAdvanceCondition, when truthy, causes the runtime engine to SKIP
 * that step for a document when the condition evaluates true.
 */

export type AutoAdvanceCondition =
  | { type: "always" }
  | { type: "classification_confidence_above"; threshold: number }
  | { type: "all_fields_confidence_above"; threshold: number }
  | { type: "field_equals"; field: string; value: string }
  // E-020 — QC sampling: hold `samplePercent`% of docs for QC, auto-advance the
  // rest. `operatorSampleRates` override the job-level rate per keying operator.
  | {
      type: "qc_sample";
      samplePercent: number;
      operatorSampleRates?: Record<string, number>;
    };

/** Pure JSON → human string. `null` means the step always runs. */
export function summarizeCondition(
  condition: AutoAdvanceCondition | null,
): string {
  if (!condition) return "always run";
  switch (condition.type) {
    case "always":
      return "skip this step (always)";
    case "classification_confidence_above":
      return `skip if classification confidence ≥ ${condition.threshold}`;
    case "all_fields_confidence_above":
      return `skip if all field confidences ≥ ${condition.threshold}`;
    case "field_equals":
      return `skip if ${condition.field} = "${condition.value}"`;
    case "qc_sample": {
      const base = `hold ${condition.samplePercent}% for QC, auto-advance the rest`;
      const overrides = Object.keys(condition.operatorSampleRates ?? {}).length;
      return overrides > 0
        ? `${base} (${overrides} operator override${overrides === 1 ? "" : "s"})`
        : base;
    }
    default:
      return "always run";
  }
}

export function RoutingConditionSummary({
  condition,
}: {
  condition: AutoAdvanceCondition | null;
}) {
  return (
    <span className="routing-condition-summary">
      {summarizeCondition(condition)}
    </span>
  );
}
